import {
  Box,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { debounce } from 'lodash';
import { useMemo } from 'react';
import * as React from 'react';

import { Button } from '@/components/form/baseInputs/Button/Button';
import { Inbox01Icon } from '@/components/icons/Inbox01Icon';
import {
  TrackUserEventModule,
  useTrackUserEvent,
} from '@/hooks/useTrackUserEvent';
import { COLORS } from '@/styles/tokens/colors';

import {
  NotificationItem,
  NotificationItemProps,
} from '../../NotificationItem/NotificationItem';

export interface NotificationTrayNotificationItem
  extends NotificationItemProps {
  id: string;
  notificationType?: string; // Add optional type for tracking
}

export interface NotificationsTabProps {
  notifications: NotificationTrayNotificationItem[];
  onClearAll: () => void;
  onMarkAllAsRead: () => void;
  loadOlder: () => void;
  hasMore: boolean;
  loadingMore: boolean;
}

export function NotificationsTab({
  notifications,
  onClearAll,
  onMarkAllAsRead,
  loadOlder,
  hasMore,
  loadingMore,
}: NotificationsTabProps) {
  const trackUserEvent = useTrackUserEvent();
  const isEmpty = notifications.length === 0;
  const urgentNotifications = notifications.filter(
    (notification) => notification.variant === 'urgent'
  );
  const regularNotifications = notifications.filter(
    (notification) => notification.variant === 'normal'
  );

  const handleClearAll = () => {
    trackUserEvent(`${TrackUserEventModule.NOTIFICATION} cleared_all`, {
      notification_count: notifications.length,
      urgent_count: urgentNotifications.length,
      regular_count: regularNotifications.length,
    });
    onClearAll();
  };

  const handleMarkAllAsRead = () => {
    const unreadCount = notifications.filter((n) => n.isUnread).length;
    trackUserEvent(`${TrackUserEventModule.NOTIFICATION} marked_all_as_read`, {
      notification_count: notifications.length,
      unread_count: unreadCount,
    });
    onMarkAllAsRead();
  };

  const createNotificationClickHandler =
    (notification: NotificationTrayNotificationItem) => () => {
      trackUserEvent(`notification clicked`, {
        notification_id: notification.id,
        notification_type: notification.notificationType || 'unknown',
        variant: notification.variant,
        is_unread: notification.isUnread,
      });
      notification.onClick();
    };

  const debouncedLoadOlder = useMemo(
    () =>
      debounce((scrollPosition: number) => {
        // Load more when user is within 200px of the bottom
        if (scrollPosition < 200 && hasMore && !loadingMore) {
          loadOlder();
        }
      }, 150),
    [hasMore, loadingMore, loadOlder]
  );

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    if (!target) return;

    const scrollPosition =
      target.scrollHeight - target.scrollTop - target.clientHeight;

    debouncedLoadOlder(scrollPosition);
  };

  if (isEmpty) {
    return (
      <Stack
        spacing={1}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          px: 2,
        }}
      >
        <Inbox01Icon size={30} color={COLORS.GRAY[300]} />
        <Typography variant="title-l" component="span">
          No notifications
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack sx={{ height: '100%' }}>
      {/* Content */}
      <Box
        sx={{
          flexGrow: 1,
          minHeight: 0,
          overflowY: 'auto',
        }}
        onScroll={handleScroll}
      >
        {/* Urgent Notifications Section */}
        {urgentNotifications.length > 0 && (
          <Stack
            spacing={0}
            divider={<Divider sx={{ borderColor: COLORS.GRAY[200] }} />}
          >
            {urgentNotifications.map((notification) => (
              <NotificationItem
                key={notification.id}
                {...notification}
                onClick={createNotificationClickHandler(notification)}
              />
            ))}
            {regularNotifications.length > 0 && (
              <Divider
                sx={{
                  borderColor: COLORS.FUNCTIONAL.WARNING[100],
                  borderWidth: 1,
                }}
              />
            )}
          </Stack>
        )}

        {/* Regular Notifications Section */}
        {regularNotifications.length > 0 && (
          <Stack
            spacing={0}
            divider={<Divider sx={{ borderColor: COLORS.GRAY[200] }} />}
          >
            {regularNotifications.map((notification) => (
              <NotificationItem
                key={notification.id}
                {...notification}
                onClick={createNotificationClickHandler(notification)}
              />
            ))}
          </Stack>
        )}

        {/* Loading indicator */}
        {loadingMore && (
          <Stack alignItems="center" py={2}>
            <CircularProgress size={16} />
          </Stack>
        )}
      </Box>

      {/* Footer */}
      <Stack
        direction="row"
        justifyContent="flex-start"
        alignItems="center"
        p={1.5}
        spacing={1}
        sx={{
          borderTop: `1px solid ${COLORS.GRAY[200]}`,
        }}
      >
        <Button
          disabled={isEmpty}
          variant="secondary"
          size="sm"
          onClick={handleMarkAllAsRead}
        >
          Mark all as read
        </Button>
        <Button
          disabled={isEmpty}
          variant="transparent"
          size="sm"
          onClick={handleClearAll}
        >
          Delete all
        </Button>
      </Stack>
    </Stack>
  );
}
